import { ChevronDown, ChevronRight, MapPin, Search } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useCountriesOptions } from '@/api/countries';
import { useEstateOptions } from '@/api/estate-options';
import { useMyIpLocation } from '@/api/me';
import { useMyTenants } from '@/api/tenant';
import { SimpleConfirmModal } from '@/components/modals/new/simple-confirmation-modal';
import { useModal } from '@/components/modals/new/use-modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MapBox } from '@/components/ui/map';
import type { SelectOption } from '@/components/ui/select';
import { Select } from '@/components/ui/select';
import Spinner from '@/components/ui/spinner';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { PROPERTY_LEVELS } from '@/constants/property-level';
import { getTenantProjects } from '@/features/tenants/api/tenant-projects';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { getCurrentPosition, isGeolocationPositionError } from '@/utils/geolocation';

import {
  getPlaceByCoordinates,
  type SuggestionMapped,
  usePlaceSuggestions,
} from '../api/place-suggestions';
import {
  type EstateAvailability,
  getEstateAvailabilityOptions,
} from '../constants/estate-availability';
import type { EstateSchemaType } from '../schemas/estate';
import type { OnSelectSuggestion } from './address-autocomplete';
import { AddressAutocomplete } from './address-autocomplete';
import { EstateFormNewUi } from './estate-form-new-ui';

interface EstateFormProps {
  children?: React.ReactNode;
  label: string;
  options?: {
    propertyLevelDisabled?: boolean;
    projectDisabled?: boolean;
    tenantDisabled?: boolean;
    isAddressOptional?: boolean;
    isAddressOpen?: boolean;
    onAddressOpenChange?: (isOpen: boolean) => void;
    fixedContentHeight?: boolean;
    hideCloseModalButton?: boolean;
    hideModalTitle?: boolean;
    /** New design only: submit button text and its text while saving */
    submitLabel?: string;
    loadingLabel?: string;
    /** Opt-in: render the form in the layout of the redesign. */
    newUi?: boolean;
  };
  onCancel?: () => void;
  onSubmit: (data: EstateSchemaType) => Promise<void> | void;
  closeModal: () => void;
}

const hasAddressInputValue = (addressData: EstateSchemaType['addressData']) =>
  Boolean(
    addressData.addressAutoComplete?.trim() ||
      addressData.address?.trim() ||
      addressData.street?.trim() ||
      addressData.houseNumber?.trim() ||
      addressData.city?.trim() ||
      addressData.postalCode?.trim()
  );

export const EstateForm: React.FC<EstateFormProps> = ({
  label,
  options = {},
  onCancel,
  onSubmit,
  closeModal,
  children,
}) => {
  const { t } = useTranslation();
  const { data: countries } = useCountriesOptions();
  const { data } = useEstateOptions();
  const { data: tenants } = useMyTenants();
  const { data: ipLocation } = useMyIpLocation();

  const tenantOptions = useMemo(
    () =>
      tenants
        .flatMap((tenant) => [tenant, ...(tenant.subTenants ?? [])])
        .map((tenant) => ({ title: tenant.name, id: tenant.id })),
    [tenants]
  );

  const { handleSubmit, watch, reset, control, formState, getValues } =
    useFormContext<EstateSchemaType>();

  const addressData = watch('addressData');
  const [selectedAddressValue, setSelectedAddressValue] = useState('');
  const propertyLevel = watch('estateData.propertyLevel');
  const tenantId = watch('estateData.tenantId');
  const projectId = watch('estateData.projectId');
  const marketType = watch('estateData.marketType');
  const availability = watch('estateData.availability');
  const isAddressOptional = options.isAddressOptional || Boolean(projectId);

  const [loading, setLoading] = useState(false);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [projectOptions, setProjectOptions] = useState<SelectOption[]>([]);
  const [isInternalAddressSectionExpanded, setIsInternalAddressSectionExpanded] = useState(false);
  const availabilityOptions = useMemo(
    () => getEstateAvailabilityOptions(t, marketType),
    [marketType, t]
  );
  const currentLocationAddressOverwriteModal = useModal();
  const [pendingCurrentLocationSuggestion, setPendingCurrentLocationSuggestion] = useState<{
    suggestion: SuggestionMapped;
    latitude: number;
    longitude: number;
  } | null>(null);
  const addressSectionRef = useRef<HTMLDivElement>(null);
  const isAddressSectionControlled = options.isAddressOpen !== undefined;
  const isAddressSectionExpanded = isAddressOptional
    ? isAddressSectionControlled
      ? Boolean(options.isAddressOpen)
      : isInternalAddressSectionExpanded
    : true;
  const hasAddressValidationError = Boolean(
    formState.errors.addressData?.address?.message ||
      formState.errors.addressData?.addressAutoComplete?.message ||
      formState.errors.addressData?.city?.message ||
      formState.errors.addressData?.countryId?.message ||
      formState.errors.addressData?.houseNumber?.message ||
      formState.errors.addressData?.postalCode?.message ||
      formState.errors.addressData?.street?.message
  );

  const getFullAddress = useCallback(
    (addressData: Omit<EstateSchemaType['addressData'], 'address'>) => {
      const { street = '', houseNumber = '', postalCode = '', city = '', countryId } = addressData;
      return [
        `${street} ${houseNumber}`.trim(),
        `${postalCode} ${city}`.trim(),
        (countries || []).find((c: { id: string }) => c.id === countryId)?.title || '',
      ]
        .filter((val) => val.length)
        .join(', ');
    },
    [countries]
  );

  useEffect(() => {
    if (tenantId) {
      getTenantProjects(tenantId).then((projects) => {
        setProjectOptions(
          projects.map((project) => ({
            title: project.name || `Project_${project.id}`,
            id: project.id,
          }))
        );
      });
    }
  }, [tenantId]);

  useEffect(() => {
    if (!isAddressOptional) {
      setIsInternalAddressSectionExpanded(false);
    }
  }, [isAddressOptional]);

  useEffect(() => {
    if (!availability || availabilityOptions.some(({ id }) => id === availability)) return;

    reset((prev) => ({
      ...prev,
      estateData: {
        ...prev.estateData,
        availability: null,
      },
    }));
  }, [availability, availabilityOptions, reset]);

  const fullAddress = useMemo(() => {
    return getFullAddress({
      houseNumber: addressData.houseNumber,
      street: addressData.street,
      city: addressData.city,
      postalCode: addressData.postalCode,
      countryId: addressData.countryId,
    });
  }, [
    getFullAddress,
    addressData.street,
    addressData.houseNumber,
    addressData.postalCode,
    addressData.city,
    addressData.countryId,
  ]);

  const debouncedFullAddress = useDebouncedValue(fullAddress, 500);

  const { data: placeSuggestions } = usePlaceSuggestions(
    isAddressSectionExpanded ? debouncedFullAddress : ''
  );
  const hasCoordinates =
    addressData.lat !== undefined &&
    addressData.lat !== null &&
    addressData.long !== undefined &&
    addressData.long !== null;

  useEffect(() => {
    if (isAddressSectionExpanded && placeSuggestions?.length) {
      reset((prev) => ({
        ...prev,
        addressData: {
          ...prev.addressData,
          lat: placeSuggestions[0].latitude,
          long: placeSuggestions[0].longitude,
        },
      }));
    }
  }, [isAddressSectionExpanded, reset, placeSuggestions]);

  useEffect(() => {
    if (!isAddressSectionExpanded) return;

    reset((prev) => ({
      ...prev,
      addressData: {
        ...prev.addressData,
        address: fullAddress,
        addressAutoComplete: fullAddress,
      },
    }));
  }, [isAddressSectionExpanded, reset, fullAddress]);

  const onSubmitHandle = handleSubmit(
    async (data) => {
      try {
        setLoading(true);
        await onSubmit({
          ...data,
          addressData:
            isAddressOptional && !isAddressSectionExpanded
              ? {
                  address: '',
                  postalCode: '',
                  city: '',
                  countryId: '',
                  street: '',
                  houseNumber: '',
                }
              : {
                  ...data.addressData,
                  address: getFullAddress(data.addressData),
                },
        });
      } finally {
        setLoading(false);
      }
      return closeModal();
    },
    (errors) => {
      if (!errors.addressData) return;

      requestAnimationFrame(() => {
        addressSectionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      });
    }
  );

  const onSelectSuggestion: OnSelectSuggestion = ({
    houseNumber,
    city,
    street,
    address,
    latitude,
    longitude,
    countryId,
    postalCode,
    countryCode,
  }) => {
    setSelectedAddressValue(address ?? '');
    reset((prev) => ({
      ...prev,
      addressData: {
        ...prev.addressData,
        ...(address && { addressAutoComplete: address }),
        street: street ?? '',
        houseNumber: houseNumber ?? '',
        city: city ?? '',
        postalCode: postalCode ?? '',
        ...(address && { address }),
        ...(countryId && { countryId }),
        // 0 is a valid coordinate, only a missing one is left out
        ...(latitude != null && { lat: latitude }),
        ...(longitude != null && { long: longitude }),
        ...(countryCode && { countryCode }),
      },
    }));
  };

  const confirmCurrentLocationAddressOverwrite = () => {
    if (!pendingCurrentLocationSuggestion) return;

    onSelectSuggestion({
      ...pendingCurrentLocationSuggestion.suggestion,
      latitude: pendingCurrentLocationSuggestion.latitude,
      longitude: pendingCurrentLocationSuggestion.longitude,
    });
    setPendingCurrentLocationSuggestion(null);
  };

  const handleUseCurrentLocation = async () => {
    if (!navigator.geolocation) {
      toast.error(t('geolocationNotSupported'));
      return;
    }

    try {
      setIsDetectingLocation(true);

      const position = await getCurrentPosition();
      const { latitude, longitude } = position.coords;
      const suggestion = await getPlaceByCoordinates({ latitude, longitude });

      if (!suggestion) {
        toast.error(t('currentLocationAddressNotFound'));
        return;
      }

      if (hasAddressInputValue(getValues('addressData'))) {
        setPendingCurrentLocationSuggestion({ suggestion, latitude, longitude });
        currentLocationAddressOverwriteModal.open();
        return;
      }

      onSelectSuggestion({
        ...suggestion,
        latitude,
        longitude,
      });
    } catch (error) {
      if (!isGeolocationPositionError(error)) {
        toast.error(t('currentLocationFailed'));
        return;
      }

      switch (error.code) {
        case error.PERMISSION_DENIED:
          toast.error(t('locationPermissionDenied'));
          return;
        case error.POSITION_UNAVAILABLE:
          toast.error(t('currentLocationUnavailable'));
          return;
        case error.TIMEOUT:
          toast.error(t('currentLocationTimeout'));
          return;
        default:
          toast.error(t('currentLocationFailed'));
      }
    } finally {
      setIsDetectingLocation(false);
    }
  };

  const onCancelHandle = () => {
    onCancel?.();
    return closeModal();
  };

  const onPropertyLevelChange = () => {
    reset((prev) => ({
      ...prev,
      estateData: {
        ...prev.estateData,
        projectId: undefined,
      },
    }));
  };

  const onToggleAddress = () => {
    if (!isAddressOptional) return;
    const nextValue = !isAddressSectionExpanded;

    if (options.onAddressOpenChange) {
      options.onAddressOpenChange(nextValue);
    } else {
      setIsInternalAddressSectionExpanded(nextValue);
    }
  };

  const currentLocationConfirmModal = (
    <SimpleConfirmModal
      modal={currentLocationAddressOverwriteModal}
      title={t('confirmCurrentLocationAddressOverwriteTitle')}
      body={t('confirmCurrentLocationAddressOverwrite')}
      onConfirm={confirmCurrentLocationAddressOverwrite}
    />
  );

  if (options.newUi) {
    return (
      <>
        <EstateFormNewUi
          control={control}
          errors={formState.errors}
          loading={loading}
          onSubmit={onSubmitHandle}
          onCancel={onCancel}
          submitLabel={options.submitLabel}
          loadingLabel={options.loadingLabel}
          propertyLevel={propertyLevel}
          addressData={addressData}
          propertyTypeOptions={data?.propertyType || []}
          marketTypeOptions={data?.buyType || []}
          projectOptions={projectOptions}
          tenantOptions={tenantOptions}
          availabilityOptions={availabilityOptions}
          countries={countries || []}
          ipLocation={ipLocation}
          propertyLevelDisabled={options.propertyLevelDisabled}
          projectDisabled={options.projectDisabled}
          tenantDisabled={options.tenantDisabled}
          isAddressOptional={isAddressOptional}
          isAddressSectionExpanded={isAddressSectionExpanded}
          hasAddressValidationError={hasAddressValidationError}
          addressSectionRef={addressSectionRef}
          onToggleAddress={onToggleAddress}
          onSelectSuggestion={onSelectSuggestion}
          onPropertyLevelChange={onPropertyLevelChange}
          selectedAddressValue={selectedAddressValue}
          isDetectingLocation={isDetectingLocation}
          onUseCurrentLocation={handleUseCurrentLocation}
        >
          {children}
        </EstateFormNewUi>
        {currentLocationConfirmModal}
      </>
    );
  }

  return (
    <form
      className="relative flex w-full min-w-0 max-w-screen-sm flex-col gap-2 overflow-auto"
      onSubmit={onSubmitHandle}
    >
      {loading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-primary-lighter/40">
          <Spinner className="size-12 [&_*]:stroke-primary-dark" />
        </div>
      )}
      {options.hideModalTitle === true ? null : <p className="text-center">{label}</p>}
      <div
        className={`no-scrollbar flex flex-col gap-2 overflow-y-scroll ${
          options.fixedContentHeight ? 'h-[min(34rem,calc(100dvh-14rem))]' : 'max-h-[34rem]'
        }`}
      >
        <div className="w-full rounded-md border border-background-lighter">
          <p className="w-full rounded-t-md bg-background-lighter px-4 py-2 sm:px-10">
            {t('general')}
          </p>
          <div className="px-4 pb-8 pt-5 sm:px-10">
            <Controller
              name="estateData.name"
              control={control}
              render={({ field, formState }) => (
                <Input
                  {...field}
                  className="!border-border bg-white text-text-secondary placeholder:text-text-secondary hover:!border-primary-light focus-visible:!border-primary-light"
                  size="lg"
                  label={t('name')}
                  errors={[formState.errors.estateData?.name?.message || '']}
                  autoFocus
                />
              )}
            />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-10">
              <Controller
                name="estateData.propertyType"
                control={control}
                render={({ field, formState }) => (
                  <Select
                    {...field}
                    color="neutral"
                    className="bg-white text-text-secondary hover:!border-primary-light focus-visible:!border-primary-light [&_svg]:text-text-primary"
                    label={t('propertyType')}
                    options={data?.propertyType || []}
                    errors={[formState.errors.estateData?.propertyType?.message || '']}
                  />
                )}
              />
              <Controller
                name="estateData.marketType"
                control={control}
                render={({ field, formState }) => (
                  <Select
                    {...field}
                    color="neutral"
                    className="bg-white text-text-secondary hover:!border-primary-light focus-visible:!border-primary-light [&_svg]:text-text-primary"
                    label={t('marketingType')}
                    options={data?.buyType || []}
                    errors={[formState.errors.estateData?.marketType?.message || '']}
                  />
                )}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-10">
              <Controller
                name="estateData.propertyLevel"
                control={control}
                render={({ field, formState }) => (
                  <Select
                    {...field}
                    color="neutral"
                    className="bg-white text-text-secondary hover:!border-primary-light focus-visible:!border-primary-light [&_svg]:text-text-primary"
                    label={`${t('propertyLevel')}*`}
                    options={[
                      {
                        title: t('project'),
                        id: PROPERTY_LEVELS.PROJECT,
                      },
                      {
                        title: t('Estate'),
                        id: PROPERTY_LEVELS.OBJECT,
                      },
                    ]}
                    errors={[formState.errors.estateData?.propertyLevel?.message || '']}
                    onChange={(value) => {
                      field.onChange(value);
                      reset((prev) => ({
                        ...prev,
                        estateData: {
                          ...prev.estateData,
                          projectId: undefined,
                        },
                      }));
                    }}
                    disabled={options.propertyLevelDisabled}
                  />
                )}
              />

              {propertyLevel === PROPERTY_LEVELS.OBJECT ? (
                <Controller
                  name="estateData.projectId"
                  control={control}
                  render={({ field, formState }) => (
                    <Select
                      {...field}
                      color="neutral"
                      className="bg-white text-text-secondary hover:!border-primary-light focus-visible:!border-primary-light [&_svg]:text-text-primary"
                      label={t('project')}
                      options={projectOptions || []}
                      errors={[formState.errors.estateData?.projectId?.message || '']}
                      disabled={options.projectDisabled}
                    />
                  )}
                />
              ) : null}
            </div>

            {propertyLevel === PROPERTY_LEVELS.OBJECT ? (
              <Controller
                name="estateData.availability"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    value={field.value ?? ''}
                    onChange={({ target }) =>
                      field.onChange((target.value || null) as EstateAvailability)
                    }
                    color="neutral"
                    className="bg-white text-text-secondary hover:!border-primary-light focus-visible:!border-primary-light [&_svg]:text-text-primary"
                    label={t('availability')}
                    options={availabilityOptions}
                    disableClear
                  />
                )}
              />
            ) : null}

            {tenantOptions.length ? (
              <Controller
                name="estateData.tenantId"
                control={control}
                render={({ field, formState }) => (
                  <Select
                    {...field}
                    color="neutral"
                    className="bg-white text-text-secondary hover:!border-primary-light focus-visible:!border-primary-light [&_svg]:text-text-primary"
                    label={t('tenant')}
                    options={tenantOptions || []}
                    errors={[formState.errors.estateData?.tenantId?.message || '']}
                    disabled={options.tenantDisabled}
                  />
                )}
              />
            ) : null}
          </div>
        </div>

        {children}
        <div
          ref={addressSectionRef}
          className="z-50 w-full rounded-md border border-background-lighter"
        >
          <button
            type="button"
            className="flex w-full items-center gap-2 rounded-t-md bg-background-lighter px-4 py-2 text-left sm:px-10"
            aria-label={
              isAddressSectionExpanded ? t('collapseAddressSection') : t('expandAddressSection')
            }
            onClick={() => {
              if (isAddressOptional) {
                const nextValue = !isAddressSectionExpanded;

                if (options.onAddressOpenChange) {
                  options.onAddressOpenChange(nextValue);
                } else {
                  setIsInternalAddressSectionExpanded(nextValue);
                }
              }
            }}
          >
            {isAddressOptional ? (
              isAddressSectionExpanded ? (
                <ChevronDown className="size-5 text-text-secondary" aria-hidden />
              ) : (
                <ChevronRight className="size-5 text-text-secondary" aria-hidden />
              )
            ) : null}
            <span>{t('address')}</span>
          </button>
          {isAddressOptional && !isAddressSectionExpanded ? (
            <div className="break-words px-4 pb-5 pt-4 text-sm sm:px-10">
              <p className="text-text-secondary">
                {addressData.addressAutoComplete || addressData.address}
              </p>
              {hasAddressValidationError ? (
                <p className="mt-2 text-red-500">{t('openAddressAndCompleteFields')}</p>
              ) : null}
            </div>
          ) : null}
          {isAddressSectionExpanded ? (
            <div className="px-4 pb-8 pt-5 sm:px-10">
              <Controller
                name="addressData.addressAutoComplete"
                control={control}
                render={({ field, formState }) => (
                  <AddressAutocomplete
                    {...field}
                    size="lg"
                    className="!border-border bg-white text-text-secondary placeholder:text-text-secondary hover:!border-primary-light focus-visible:!border-primary-light"
                    startIcon={<Search color="#9CA3AF" aria-hidden />}
                    endIcon={
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            rounded
                            loading={isDetectingLocation}
                            disabled={isDetectingLocation}
                            aria-label={t('useCurrentLocation')}
                            className="border-0 text-primary-light hover:border-0 hover:bg-transparent hover:text-primary"
                            onClick={handleUseCurrentLocation}
                          >
                            <MapPin aria-hidden />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent className="border border-primary-light bg-white text-primary-dark shadow-sm">
                          {t('useCurrentLocation')}
                        </TooltipContent>
                      </Tooltip>
                    }
                    label={t('address')}
                    selectedValue={selectedAddressValue}
                    onSelectSuggestion={onSelectSuggestion}
                    errors={[formState.errors.addressData?.addressAutoComplete?.message || '']}
                  />
                )}
              />
              <div className="mt-4 h-48 w-full overflow-hidden rounded bg-background-lighter [&_.mapboxgl-canvas]:!h-full [&_.mapboxgl-canvas]:!w-full [&_.mapboxgl-map]:!h-full [&_.mapboxgl-map]:!w-full">
                <MapBox
                  key={
                    hasCoordinates ? `${addressData.lat}:${addressData.long}` : ipLocation?.latitude
                  }
                  fullscreenControl={false}
                  zoom={hasCoordinates ? 12 : 4}
                  {...(hasCoordinates
                    ? {
                        latitude: Number(addressData.lat),
                        longitude: Number(addressData.long),
                      }
                    : ipLocation
                      ? {
                          latitude: ipLocation.latitude,
                          longitude: ipLocation.longitude,
                        }
                      : undefined)}
                />
              </div>
              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-x-10 sm:gap-y-0">
                <Controller
                  name="addressData.houseNumber"
                  control={control}
                  render={({ field, formState }) => (
                    <Input
                      {...field}
                      className="!border-border bg-white text-text-secondary placeholder:text-text-secondary hover:!border-primary-light focus-visible:!border-primary-light"
                      size="lg"
                      label={t('houseNumber')}
                      errors={[formState.errors.addressData?.houseNumber?.message || '']}
                    />
                  )}
                />
                <Controller
                  name="addressData.street"
                  control={control}
                  render={({ field, formState }) => (
                    <Input
                      {...field}
                      className="!border-border bg-white text-text-secondary placeholder:text-text-secondary hover:!border-primary-light focus-visible:!border-primary-light"
                      size="lg"
                      label={t('street')}
                      errors={[formState.errors.addressData?.street?.message || '']}
                    />
                  )}
                />
                <Controller
                  name="addressData.city"
                  control={control}
                  render={({ field, formState }) => (
                    <Input
                      {...field}
                      className="!border-border bg-white text-text-secondary placeholder:text-text-secondary hover:!border-primary-light focus-visible:!border-primary-light"
                      size="lg"
                      label={t('city')}
                      errors={[formState.errors.addressData?.city?.message || '']}
                    />
                  )}
                />
                <Controller
                  name="addressData.countryId"
                  control={control}
                  render={({ field, formState }) => (
                    <Select
                      {...field}
                      size="lg"
                      color="neutral"
                      className="bg-white text-text-secondary hover:!border-primary-light focus-visible:!border-primary-light [&_svg]:text-text-primary"
                      label={t('country')}
                      options={countries || []}
                      errors={[formState.errors.addressData?.countryId?.message || '']}
                    />
                  )}
                />
                <Controller
                  name="addressData.postalCode"
                  control={control}
                  render={({ field, formState }) => (
                    <Input
                      {...field}
                      className="!border-border bg-white text-text-secondary placeholder:text-text-secondary hover:!border-primary-light focus-visible:!border-primary-light"
                      size="lg"
                      label={t('postalCode')}
                      errors={[formState.errors.addressData?.postalCode?.message || '']}
                    />
                  )}
                />
              </div>
            </div>
          ) : null}
        </div>
      </div>
      <div className="mt-6 grid grid-cols-2 gap-1">
        <Button variant="outline" color="primary" onClick={onCancelHandle}>
          {t('cancel')}
        </Button>
        <Button type="submit">{t('submit')}</Button>
      </div>
      {currentLocationConfirmModal}
    </form>
  );
};
